import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { UserCheck, Wallet, Link2, ShieldCheck, AlertCircle, ExternalLink, FlaskConical, Copy, Check, Loader2 } from "lucide-react";

function DemoRestaurantSection({ adminAxios }) {
  const [loading, setLoading] = useState(false);
  const [demo, setDemo] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleCreate = async () => {
    setLoading(true);
    try {
      const { data } = await adminAxios.post("/platform-admin/demo/create-restaurant");
      setDemo(data);
      toast.success("Restaurante demo criado com sucesso!");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erro ao criar restaurante demo";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyAll = () => {
    if (!demo) return;
    navigator.clipboard.writeText(demo.copy_text);
    setCopied(true);
    toast.success("Dados copiados!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Card className="border-amber-200 bg-amber-50/40">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <FlaskConical className="w-4 h-4 text-amber-600" />
            <span className="font-semibold text-sm">Restaurante Demo para Afiliados</span>
            <Badge className="text-[9px] bg-amber-100 text-amber-700">Sandbox</Badge>
          </div>
          {!demo && (
            <Button
              size="sm"
              className="bg-amber-600 hover:bg-amber-700 text-white text-xs gap-1.5"
              onClick={handleCreate}
              disabled={loading}
              data-testid="create-demo-restaurant-btn"
            >
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <FlaskConical className="w-3 h-3" />}
              {loading ? "Criando..." : "Criar Restaurante Demo"}
            </Button>
          )}
        </div>

        {!demo && !loading && (
          <p className="text-xs text-muted-foreground">
            Cria um restaurante 100% funcional e isolado — com menu, pedidos simulados e caixa aberto.
            Ideal para afiliados demonstrarem o sistema ao cliente.
          </p>
        )}

        {demo && (
          <div className="space-y-3 mt-1">
            {/* URL pública do menu demo */}
            <div className="bg-white rounded-lg p-3 border border-amber-100 text-xs">
              <p className="text-muted-foreground font-medium mb-1">URL do Restaurante Demo</p>
              <p className="font-mono text-amber-700 break-all">{demo.demo_url}</p>
            </div>

            {/* Credenciais de acesso ao painel */}
            <div className="bg-white rounded-lg p-3 border border-amber-100 text-xs space-y-1.5">
              <p className="text-muted-foreground font-medium">Acesso ao Painel Admin</p>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground w-12">Email</span>
                <span className="font-mono text-[11px] flex-1 text-right break-all">{demo.login.email}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground w-12">Senha</span>
                <span className="font-mono font-bold text-sm tracking-widest flex-1 text-right">{demo.login.password}</span>
              </div>
              <div className="flex items-center justify-between pt-0.5">
                <span className="text-muted-foreground w-12">URL Login</span>
                <span className="font-mono text-[11px] flex-1 text-right text-blue-600">{demo.login.access_url}</span>
              </div>
            </div>

            {/* Stats */}
            <div className="flex items-center gap-3 text-[11px] text-muted-foreground bg-white rounded-lg p-2.5 border border-amber-100">
              <span>📦 {demo.summary.products} produtos</span>
              <span>🗂️ {demo.summary.categories} categorias</span>
              <span>🛒 {demo.summary.orders} pedidos</span>
              <span className="ml-auto text-amber-600 font-medium">Expira em 7 dias</span>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                size="sm"
                className="text-xs gap-1.5 bg-amber-600 hover:bg-amber-700 text-white"
                onClick={() => window.open(demo.login.access_url, "_blank")}
                data-testid="open-demo-btn"
              >
                <ExternalLink className="w-3 h-3" /> Abrir demo
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-xs gap-1.5 border-amber-200 hover:bg-amber-50"
                onClick={handleCopyAll}
                data-testid="copy-demo-credentials-btn"
              >
                {copied ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                {copied ? "Copiado!" : "Copiar tudo"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-xs gap-1.5 border-amber-200 hover:bg-amber-50"
                onClick={() => { setDemo(null); }}
                data-testid="create-another-demo-btn"
              >
                <FlaskConical className="w-3 h-3" /> Criar outro
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function AffiliatesTab({ adminAxios, affiliates, onRefreshTab }) {
  return (
    <div className="space-y-4">
      <DemoRestaurantSection adminAxios={adminAxios} />

      <div className="flex items-center justify-between">
        <h2 className="font-bold">Programa de Afiliados</h2>
        <div className="flex items-center gap-2">
          <Badge variant="outline">{affiliates.length} afiliados</Badge>
          <Button
            size="sm"
            variant="outline"
            className="text-xs gap-1"
            onClick={() => {
              navigator.clipboard.writeText(`${window.location.origin}/affiliate`);
              toast.success("Link copiado! (landing page do programa)");
            }}
            data-testid="copy-affiliate-signup-link"
          >
            <Link2 className="w-3 h-3" /> Copiar Link
          </Button>
        </div>
      </div>

      {affiliates.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            <UserCheck className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p>Nenhum afiliado cadastrado ainda</p>
            <p className="text-xs mt-1">Compartilhe o link: <span className="font-mono text-orange-600">{window.location.origin}/affiliate</span></p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {affiliates.map((a) => (
            <Card key={a.id} data-testid={`admin-affiliate-${a.id?.slice(-6)}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center text-amber-700 font-bold text-sm flex-shrink-0">
                    {a.name?.charAt(0)?.toUpperCase() || "A"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-bold text-sm">{a.name || a.email}</p>
                      <Badge className={`text-[9px] ${a.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                        {a.status === "active" ? "Ativo" : "Bloqueado"}
                      </Badge>
                      {/* Stripe Connect status */}
                      {a.stripe_onboarding_complete ? (
                        <Badge className="text-[9px] bg-blue-100 text-blue-700 gap-1 flex items-center">
                          <ShieldCheck className="w-2.5 h-2.5" /> Stripe OK
                        </Badge>
                      ) : (
                        <Badge className="text-[9px] bg-amber-100 text-amber-700 gap-1 flex items-center">
                          <AlertCircle className="w-2.5 h-2.5" /> Sem conta bancaria
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 text-[11px] text-muted-foreground flex-wrap">
                      <span>{a.email}</span>
                      <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-[10px]">{a.code}</span>
                      {a.phone && <span>{a.phone}</span>}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-center flex-shrink-0">
                    <div><p className="text-sm font-bold">{a.total_referrals}</p><p className="text-[10px] text-muted-foreground">Indicacoes</p></div>
                    <div><p className="text-sm font-bold text-emerald-600">{a.total_commission?.toFixed(2)} EUR</p><p className="text-[10px] text-muted-foreground">Total</p></div>
                    <div><p className="text-sm font-bold text-amber-600">{a.pending_commission?.toFixed(2)} EUR</p><p className="text-[10px] text-muted-foreground">Pendente</p></div>
                  </div>
                </div>

                <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
                  <div className="flex items-center gap-2 flex-1">
                    <span className="text-xs text-muted-foreground font-medium">Comissao:</span>
                    <select
                      value={a.commission_type || "percentage"}
                      onChange={async (e) => {
                        try {
                          await adminAxios.patch(`/platform-admin/affiliates/${a.id}`, { commission_type: e.target.value });
                          toast.success("Tipo de comissao atualizado");
                          onRefreshTab("affiliates");
                        } catch { toast.error("Erro"); }
                      }}
                      className="text-xs border rounded px-2 py-1 bg-white"
                      data-testid={`affiliate-commission-type-${a.id?.slice(-6)}`}
                    >
                      <option value="percentage">Porcentagem (%)</option>
                      <option value="fixed">Valor Fixo (EUR)</option>
                    </select>
                    <div className="flex items-center gap-1">
                      <Input
                        type="number"
                        step={a.commission_type === "fixed" ? "1" : "1"}
                        min={a.commission_type === "fixed" ? "1" : "5"}
                        max={a.commission_type === "fixed" ? "200" : "50"}
                        defaultValue={a.commission_value ?? 20}
                        className="w-20 h-8 text-xs"
                        onBlur={async (e) => {
                          let val = parseFloat(e.target.value) || 0;
                          if (a.commission_type === "percentage") val = Math.min(50, Math.max(5, val));
                          if (a.commission_type === "fixed") val = Math.max(1, val);
                          e.target.value = val;
                          if (val === a.commission_value) return;
                          try {
                            await adminAxios.patch(`/platform-admin/affiliates/${a.id}`, { commission_value: val });
                            toast.success("Valor atualizado");
                            onRefreshTab("affiliates");
                          } catch { toast.error("Erro"); }
                        }}
                        data-testid={`affiliate-commission-value-${a.id?.slice(-6)}`}
                      />
                      <span className="text-xs text-muted-foreground">{a.commission_type === "fixed" ? "EUR" : "%"}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant={a.status === "active" ? "outline" : "default"}
                      className={`text-[10px] h-7 px-2 ${a.status === "active" ? "text-red-500 border-red-200 hover:bg-red-50" : "bg-emerald-600 text-white"}`}
                      onClick={async () => {
                        try {
                          await adminAxios.patch(`/platform-admin/affiliates/${a.id}`, { status: a.status === "active" ? "blocked" : "active" });
                          toast.success(a.status === "active" ? "Afiliado bloqueado" : "Afiliado reativado");
                          onRefreshTab("affiliates");
                        } catch { toast.error("Erro"); }
                      }}
                      data-testid={`affiliate-status-${a.id?.slice(-6)}`}
                    >
                      {a.status === "active" ? "Bloquear" : "Reativar"}
                    </Button>
                    {a.pending_commission > 0 && (
                      <Button
                        size="sm"
                        className="text-[10px] h-7 px-2 bg-emerald-600 hover:bg-emerald-700 text-white gap-1"
                        onClick={async () => {
                          try {
                            await adminAxios.post(`/platform-admin/affiliates/${a.id}/pay`, { amount: a.pending_commission });
                            toast.success(`${a.pending_commission.toFixed(2)} EUR marcado como pago`);
                            onRefreshTab("affiliates");
                          } catch { toast.error("Erro"); }
                        }}
                        data-testid={`pay-affiliate-${a.id?.slice(-6)}`}
                      >
                        <Wallet className="w-3 h-3" /> Pagar {a.pending_commission.toFixed(2)} EUR
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
